/** Step 4: the ITSM ticket that now exists (or already did), and "Migrate another". */

import { CircleCheck, Icon } from "@norican/icons";
import { Alert, Button, Typography } from "@norican/react";
import { Link } from "react-router";

import { SectionCard } from "@/components/common";
import { useT } from "@/i18n";
import type { MigrateImportResponse } from "@/lib/api";

export interface DoneStepProps {
  result: MigrateImportResponse;
  onAnother: () => void;
}

export function DoneStep({ result, onAnother }: DoneStepProps) {
  const { t } = useT("tickets");
  const record = result.migration;
  if (record === null || record === undefined) {
    return null;
  }
  const migrated = result.status === "migrated";
  const skipped = result.files_skipped ?? [];

  return (
    <SectionCard>
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <Icon icon={CircleCheck} size="md" tone={migrated ? "success" : "warning"} />
          <Typography as="h2" variant="heading-4">
            {migrated
              ? t("migrate.done.title", {
                  number: record.ticket_number,
                  customer: record.customer_name,
                })
              : t("migrate.done.alreadyTitle", { number: record.ticket_number })}
          </Typography>
        </div>
        {migrated ? (
          <>
            {/* The ticket belongs to the customer; the migrator's read access
                comes only from having imported it (`viewed_as_migrator`). */}
            <Typography variant="body-sm">{t("migrate.done.canView")}</Typography>
            <Typography variant="body-sm" tone="muted">
              {t("migrate.done.filesUploaded", { n: result.files_uploaded ?? 0 })}
            </Typography>
          </>
        ) : null}
        {/* The import hands the ticket to the migrate team, owned by the
            migrator. When Ivanti refuses either half, say what is left to do. */}
        {migrated &&
        result.assignment_enabled === true &&
        result.assigned_team &&
        result.assigned_to ? (
          <Typography variant="body-sm">
            {t("migrate.done.assigned", { team: result.assigned_team })}
          </Typography>
        ) : null}
        {migrated &&
        result.assignment_enabled === true &&
        result.assigned_team &&
        !result.assigned_to ? (
          <Alert tone="warning">{t("migrate.done.teamOnly", { team: result.assigned_team })}</Alert>
        ) : null}
        {migrated && result.assignment_enabled === true && !result.assigned_team ? (
          <Alert tone="warning">{t("migrate.done.notAssigned")}</Alert>
        ) : null}
        {migrated && !record.note_written ? (
          <Alert tone="warning">{t("migrate.done.noteMissing")}</Alert>
        ) : null}
        {skipped.length > 0 ? (
          <Alert tone="info" title={t("migrate.done.skippedTitle")}>
            <ul className="list-disc pl-5">
              {skipped.map((file) => (
                <li key={`${file.name}-${file.reason}`}>
                  {file.name} — {file.reason}
                </li>
              ))}
            </ul>
          </Alert>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button render={<Link to={record.url_path} />}>{t("migrate.done.open")}</Button>
          <Button variant="outline" tone="neutral" onClick={onAnother}>
            {t("migrate.done.another")}
          </Button>
        </div>
      </div>
    </SectionCard>
  );
}
